/**
 * Bingo Progress Dashboard Component
 *
 * Displays progress statistics and visualizations for a bingo card.
 */

import type { BingoProgress } from '@/types/bingo';
import { TrendingUp, Calendar, CheckCircle2, Target } from 'lucide-react';

interface BingoProgressDashboardProps {
  progress: BingoProgress;
}

export function BingoProgressDashboard({ progress }: BingoProgressDashboardProps) {
  const {
    completedCells,
    inProgressCells,
    notStartedCells,
    completionPercentage,
    patterns,
    timeRemaining,
    isBlackout,
  } = progress;

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Progress Dashboard
      </h2>

      {/* Overall Progress */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Overall Progress</h3>
          <span className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {completionPercentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5 mb-5 shadow-inner">
          <div
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-5 rounded-full transition-all duration-500 shadow-md"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-xl border border-green-200">
            <div className="text-green-700 text-xs font-medium mb-1">Completed</div>
            <div className="text-2xl font-bold text-green-600">{completedCells}</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-3 rounded-xl border border-blue-200">
            <div className="text-blue-700 text-xs font-medium mb-1">In Progress</div>
            <div className="text-2xl font-bold text-blue-600">{inProgressCells}</div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-3 rounded-xl border border-gray-200">
            <div className="text-gray-600 text-xs font-medium mb-1">Not Started</div>
            <div className="text-2xl font-bold text-gray-600">{notStartedCells}</div>
          </div>
        </div>
      </div>

      {/* Patterns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white/70 backdrop-blur-sm rounded-xl shadow-lg p-4 border border-white/50 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg">
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-800 text-sm">Rows</h3>
          </div>
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              {patterns.rows.completed}
            </span>
            <span className="text-gray-500 text-sm">/ {patterns.rows.total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(patterns.rows.completed / patterns.rows.total) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-xl shadow-lg p-4 border border-white/50 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg">
              <Target className="w-4 h-4 text-purple-600" />
            </div>
            <h3 className="font-bold text-gray-800 text-sm">Columns</h3>
          </div>
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
              {patterns.columns.completed}
            </span>
            <span className="text-gray-500 text-sm">/ {patterns.columns.total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
            <div
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(patterns.columns.completed / patterns.columns.total) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-xl shadow-lg p-4 border border-white/50 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-gradient-to-br from-green-100 to-emerald-200 rounded-lg">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="font-bold text-gray-800 text-sm">Diagonals</h3>
          </div>
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent">
              {patterns.diagonals.completed}
            </span>
            <span className="text-gray-500 text-sm">/ {patterns.diagonals.total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
            <div
              className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(patterns.diagonals.completed / patterns.diagonals.total) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Time Remaining */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg">
            <Calendar className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="font-bold text-gray-800">Time Remaining</h3>
        </div>
        <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-500 bg-clip-text text-transparent">
          {timeRemaining.days}
          <span className="text-2xl ml-1">days</span>
        </div>
        <div className="text-sm text-gray-600 mt-2">Until end of {timeRemaining.year}</div>
      </div>

      {/* Blackout Status */}
      {isBlackout && (
        <div className="bg-gradient-to-br from-yellow-100 via-yellow-50 to-orange-100 rounded-2xl shadow-xl p-6 border-2 border-yellow-300 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-yellow-900 text-xl mb-1">Blackout Achieved! 🎉</h3>
              <p className="text-sm text-yellow-800">You've completed every goal on your card!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

